import React from 'react';

import { ArrowLeft, Map as MapIcon } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import ClosedBadge from '../../components/common/ClosedBadge';
import Loader from '../../components/common/Loader';
import CourseContactLinks from '../../components/maps/CourseContactLinks';
import CourseHero from '../../components/maps/CourseHero';
import ShareCourseButton from '../../components/maps/ShareCourseButton';
import { useCourseProfile } from '../../hooks/useCourseProfile';

// A course's own read-only page, at /courses/:id. Public: no sign-in, and its data comes from an
// endpoint on SecuredMiddleware's whitelist, because a shared Courses Played list (a later story)
// will link here for people with no account. Deliberately not the admin edit form with its fields
// disabled: this is the member-facing view of a club, so it is built from the same parts as the
// Explore map's course drawer (CourseHero, CourseContactLinks, ShareCourseButton) and reads the
// same way, in the same charcoal and gold.
//
// It shows only what the map shows for a course, from the same data (CourseMinifiedDTO). There is
// nothing member-specific on it, so it looks the same to everyone who opens the link.
const CourseProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: course, isLoading, isError } = useCourseProfile(id);

  // Back where they came from when there is somewhere to go back to (the member's Courses Played
  // list, usually); the map when the page was opened straight from a link.
  const handleBack = (): void => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/explore');
    }
  };

  const locationLine = [course?.stateName, course?.countryName]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="min-h-screen bg-charcoal text-white">
      <header className="flex items-center justify-between gap-3 border-b border-white/10 bg-charcoal-light px-4 py-3">
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center gap-1.5 rounded-full px-2 py-1 text-sm text-white/70 transition-colors hover:bg-white/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <Link to="/explore" aria-label="GolfPlayed, the course map">
          <img
            src="/images/logo-white.png"
            alt="GolfPlayed"
            className="h-7 w-36 object-contain object-right"
          />
        </Link>
      </header>

      <main className="mx-auto w-full max-w-xl px-4 py-6 sm:py-10">
        {isLoading && (
          <div className="flex justify-center py-16">
            <Loader />
          </div>
        )}

        {!isLoading && (isError || course == null) && (
          <div className="rounded-xl border border-white/10 bg-charcoal-light px-6 py-10 text-center">
            <h1 className="text-lg font-semibold">
              {isError
                ? 'Could not load this course.'
                : 'That course could not be found.'}
            </h1>
            <p className="mt-2 text-sm text-white/60">
              {isError
                ? 'Check your connection and try again.'
                : 'It may have closed or been removed from GolfPlayed.'}
            </p>
            <Link
              to="/explore"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-secondary transition hover:bg-opacity-90"
            >
              <MapIcon className="h-4 w-4" />
              Explore the map
            </Link>
          </div>
        )}

        {!isLoading && course != null && (
          <article className="overflow-hidden rounded-xl border border-primary/30 bg-charcoal-light shadow-2xl">
            <CourseHero course={course} distanceLabel={null} eager />

            {/* pt-8 clears the logo plate, which hangs 1.25rem below the hero (see CourseHero). */}
            <div className="px-5 pb-6 pt-8">
              <div className="flex flex-wrap items-center gap-1.5">
                {locationLine && (
                  <span className="inline-block rounded border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                    {locationLine}
                  </span>
                )}
                <ClosedBadge
                  closed={course.closed}
                  closedYear={course.closedYear}
                  closedMonth={course.closedMonth}
                  className="py-1"
                />
              </div>

              <h1 className="mt-2 text-xl font-bold leading-snug text-white">
                {course.name}
              </h1>

              <CourseContactLinks course={course} className="mt-3" />

              <ShareCourseButton course={course} className="mt-4 w-full" />
            </div>
          </article>
        )}
      </main>
    </div>
  );
};

export default CourseProfile;
